import { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";

import useAxios from "../../utils/useAxios";
import { resObj, paginationVal } from "../../utils/constants";
import PermissionContext from "../../context/PermissionContext";
import DrawerContext from "../../context/DrawerContext";
import AutoLogout from "../../utils/useAutoLogout";

import Pagination from '@mui/material/Pagination';

import SideDrawer from "../../components/layout/SideDrawer";
import Loader from "../../components/layout/Loader";
import ListHeader from "../../components/layout/ListHeader";
import CustomTable from "../../components/layout/CustomTable";
import SuccessAlert from "../../components/layout/SuccessAlert";
import NoData from "../../components/layout/NoData";
import ErrorModal from "../../components/layout/ErrorModal";
import InputModalForm from "../../components/layout/InputModalForm";
import DeleteModal from "../../components/layout/DeleteModal";

const OrganisationsListPage = () => {
  const api = useAxios();
  const navigate = useNavigate();

  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  useEffect(() => {
    if(permisionsList.length > 0) checkUserPermission(['Admin-View Organisation List']);
  }, [permisionsList]);

  const tableHeading = ['SI No', 'Organisation Name', 'File Types'];
  const deleteModalTitle = 'Organisation Delete';
  const deleteModalMsg = 'Are you sure you want to delete (Organisation will be lost)?';

  const pageData = useRef({...paginationVal});
  const [apiRes, setApiRes] = useState(resObj);
  const [organisationList, setOrganisationList] = useState([]);
  const [organisationId, setOrganisationId] = useState('');
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [organisationName, setOrganisationName] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    getOrganisations();
  }, []);

  const getOrganisations = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/user/organisation/list/get', {
        params: { 
          search_term: pageData.current.searchTerm,
          page_number: +pageData.current.pageNumber,
        }
      });
      
      if (response.status === 200) {
        setApiRes({ ...apiRes, loading: false });
        setOrganisationList(response.data.data);
        pageData.current.pageCount = response.data.page_count;
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Logs Get',
        loading: false,
      });
    }
  };

  const navigateToEdit = oid => {
    navigate(`/organisation/edit/${oid}`);
  };

  const organisationDelete = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.delete(`/api/user/organisation/delete/${organisationId}`);

      if (response.status === 200) {
        setDeleteModalOpen(false);
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'Organisation deleted successfully',
          loading: false,
        });
      }
      getOrganisations();
    } catch (err) {
      setDeleteModalOpen(false);
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Organisation Delete',
        loading: false,
      });
    }
  };

  const handlePaginationClick = (e, pageNumber) => {
    pageData.current.pageNumber = pageNumber;
    getOrganisations();
  };

  const organisationCreate = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.post('/api/user/organisation/create', {
        organisation_name: organisationName
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        setShowOrgModal(false);
        setOrganisationName('');
        getOrganisations();
      }
    } catch (err) {
      const errMsg = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '');
      if (errMsg === 'Organisation already exists.') {
        setFormErrors({ 
          organisationName: 'Organisation already exists.' 
        });
        setApiRes({ ...apiRes, loading: false });
      }
      else {
        setShowOrgModal(false);
        setOrganisationName('');
        setApiRes({
          ...apiRes,
          axiosError: true,
          errMsg: errMsg,
          errHeading: 'Organisation Create',
          loading: false,
        });
      }
    }
  };

  const validate = () => {
    const errors = {};
    if (!organisationName) {
      errors.organisationName = 'Organisation name is required!';
    }
    return errors;
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      organisationCreate();
    }
  }, [formErrors]);

  const handleSubmit = e => {
    e.preventDefault();
    setFormErrors(validate());
    setIsSubmit(true);
  };

  return(
    <AutoLogout>
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      {apiRes.loading && <Loader />}
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <ListHeader 
          title="Organisations" 
          handleClick={() => setShowOrgModal(true)} 
          search={getOrganisations}
          pageData={pageData}
          hasCreatePermission={permisionsList.includes('Admin-Organisation Create')}
        />

        <CustomTable 
          heading={tableHeading} 
          rows={organisationList} 
          navigateToEdit={navigateToEdit}
          setDeleteModalOpen={setDeleteModalOpen}
          setId={setOrganisationId}
          pageData={pageData}
          idKey={'id'}
          isDeleteBtnVisible={permisionsList.includes('Admin-Manage Organisation')}
          showToolTip={false}
        />

        {organisationList.length === 0 && <NoData />}

        {
          (organisationList.length !== 0 && pageData.current.pageCount !== 1) 
            && 
          <Pagination 
            style={{marginTop: '10px', float: 'right'}} 
            count={pageData.current.pageCount} 
            onChange={handlePaginationClick}
          />
        }

        <InputModalForm 
          title={'Organisation Create'} 
          label={'Organisation Name'} 
          show={showOrgModal} 
          setShow={setShowOrgModal} 
          value={organisationName} 
          setValue={setOrganisationName} 
          formErrors={formErrors} 
          errorKey={'organisationName'} 
          handleSubmit={handleSubmit} 
        />

        <DeleteModal
          title={deleteModalTitle} 
          message={deleteModalMsg} 
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDelete={organisationDelete}
        />

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />

      </div>
    </AutoLogout>
  );
};

export default OrganisationsListPage;